import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';
import { toast } from 'sonner';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { MediaSelector } from '../../components/media/MediaSelector';
import { 
  Upload, 
  Image as ImageIcon, 
  Copy, 
  Trash2, 
  X, 
  Search,
  Grid3X3,
  List,
  Download,
  Eye,
  Loader2,
  FolderOpen
} from 'lucide-react';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"];

interface MediaFile {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  created_at: string;
  updated_at: string;
}

export default function MediaLibrary() {
  const { tenant } = useTenantContext();
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tenant) {
      fetchMediaFiles();
    }
  }, [tenant]);

  const fetchMediaFiles = async () => {
    if (!tenant) return;
    
    try {
      setLoading(true);
      
      // Lister les fichiers dans le dossier tenant/{tenant_id}
      const { data: fileList, error } = await supabase.storage
        .from('media')
        .list(`tenant/${tenant.id}`, {
          limit: 1000,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Erreur lors de la récupération des fichiers:', error);
        toast.error('Erreur lors du chargement des fichiers');
        return;
      }

      // Convertir en format MediaFile avec URLs publiques
      const mediaFiles: MediaFile[] = fileList
        .filter(file => file.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const { data } = supabase.storage
            .from('media')
            .getPublicUrl(`tenant/${tenant.id}/${file.name}`);
          
          return {
            id: file.id || file.name,
            name: file.name,
            url: data.publicUrl,
            size: file.metadata?.size || 0,
            type: file.metadata?.mimetype || 'image/jpeg',
            created_at: file.created_at || new Date().toISOString(),
            updated_at: file.updated_at || new Date().toISOString()
          };
        });

      setFiles(mediaFiles);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des fichiers');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (uploadFiles: FileList | File[]) => {
    if (!tenant) return;

    const fileArray = Array.from(uploadFiles);
    const validFiles = fileArray.filter(file => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`Type de fichier non supporté: ${file.name}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Fichier trop volumineux: ${file.name} (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    
    try {
      const uploadPromises = validFiles.map(async (file) => {
        // Générer un nom unique pour éviter les conflits
        const fileExtension = file.name.split('.').pop();
        const uniqueName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
        const filePath = `tenant/${tenant.id}/${uniqueName}`;

        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        return uniqueName;
      });

      await Promise.all(uploadPromises);
      
      toast.success(`${validFiles.length} fichier(s) uploadé(s) avec succès`);
      await fetchMediaFiles();
      
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur lors de l\'upload des fichiers');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const copyToClipboard = async (url: string, fileName: string) => {
    try {
      // Méthode moderne avec l'API Clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success(`URL copiée: ${fileName}`);
      } else {
        // Fallback pour les contextes non-sécurisés ou navigateurs anciens
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success(`URL copiée: ${fileName}`);
        } else {
          throw new Error('Échec de la copie');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      toast.error('Erreur lors de la copie. Veuillez copier manuellement l\'URL.');
    }
  };

  const deleteFile = async (fileName: string) => {
    if (!tenant) return;
    
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${fileName}" ?`)) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from('media')
        .remove([`tenant/${tenant.id}/${fileName}`]);

      if (error) {
        throw error;
      }

      toast.success('Fichier supprimé');
      await fetchMediaFiles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteSelectedFiles = async () => {
    if (!tenant || selectedFiles.length === 0) return;
    
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${selectedFiles.length} fichier(s) ?`)) {
      return;
    }

    try {
      const filePaths = selectedFiles.map(fileName => `tenant/${tenant.id}/${fileName}`);
      
      const { error } = await supabase.storage
        .from('media')
        .remove(filePaths);

      if (error) {
        throw error;
      }

      toast.success(`${selectedFiles.length} fichier(s) supprimé(s)`);
      setSelectedFiles([]);
      await fetchMediaFiles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleFileSelection = (fileName: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileName) 
        ? prev.filter(f => f !== fileName)
        : [...prev, fileName]
    );
  };

  const selectAllFiles = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredFiles.map(f => f.name));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Médiathèque
              </h1>
              <p className="text-gray-400">
                Gérez vos images et fichiers média
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Uploader
              </button>
              <button
                onClick={() => setShowMediaSelector(true)}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
              >
                <ImageIcon className="h-4 w-4" />
                Ouvrir en modale
              </button>
            </div>
          </div>
        </div>

        {/* Zone d'upload */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 mb-8 text-center transition-colors ${
            dragOver 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-white/20 bg-white/5'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
            className="hidden"
          />
          
          <div className="flex flex-col items-center">
            <Upload className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-white font-medium mb-2">
              Glissez-déposez vos images ici ou cliquez pour sélectionner
            </p>
            <p className="text-gray-400 text-sm">
              Formats supportés: JPG, PNG, WebP, SVG (max 10MB)
            </p>
          </div>
        </div>

        {/* Barre d'outils */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {selectedFiles.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">
                  {selectedFiles.length} sélectionné(s)
                </span>
                <button
                  onClick={deleteSelectedFiles}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Supprimer
                </button>
              </div>
            )}
            
            <button
              onClick={selectAllFiles}
              className="text-sm text-gray-400 hover:text-white"
            >
              {selectedFiles.length === filteredFiles.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            
            <span className="text-sm text-gray-400">
              {filteredFiles.length} fichier(s)
            </span>
          </div>
        </div>

        {/* Liste des fichiers */}
        {filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {searchTerm ? 'Aucun fichier trouvé' : 'Aucun fichier dans votre médiathèque'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              {searchTerm ? 'Essayez un autre terme de recherche' : 'Commencez par uploader vos premières images'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className={`relative group bg-white/5 rounded-lg overflow-hidden border-2 transition-all ${
                  selectedFiles.includes(file.name)
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                <div className="aspect-square relative">
                  <img
                    src={file.url}
                    alt={file.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  
                  {/* Overlay avec actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(file.url, file.name)}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                        title="Copier l'URL"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                        title="Voir en grand"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteFile(file.name)}
                        className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Checkbox de sélection */}
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.name)}
                      onChange={() => toggleFileSelection(file.name)}
                      className="w-4 h-4 text-amber-600 bg-white/20 border-white/30 rounded focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Informations du fichier */}
                <div className="p-3">
                  <p className="text-white text-sm font-medium truncate" title={file.name}>
                    {file.name}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/5 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedFiles.length === filteredFiles.length && filteredFiles.length > 0}
                      onChange={selectAllFiles}
                      className="w-4 h-4 text-amber-600 bg-white/20 border-white/30 rounded focus:ring-amber-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Aperçu</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Nom</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Taille</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredFiles.map((file) => (
                  <tr key={file.id} className="hover:bg-white/5">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.name)}
                        onChange={() => toggleFileSelection(file.name)}
                        className="w-4 h-4 text-amber-600 bg-white/20 border-white/30 rounded focus:ring-amber-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-12 h-12 object-cover rounded"
                        loading="lazy"
                      />
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{file.name}</td>
                    <td className="px-4 py-3 text-gray-400">{formatFileSize(file.size)}</td>
                    <td className="px-4 py-3 text-gray-400">
                      {new Date(file.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => copyToClipboard(file.url, file.name)}
                          className="p-1 text-gray-400 hover:text-white transition-colors"
                          title="Copier l'URL"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => window.open(file.url, '_blank')}
                          className="p-1 text-gray-400 hover:text-white transition-colors"
                          title="Voir en grand"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteFile(file.name)}
                          className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Sélecteur de média en modale */}
        <MediaSelector
          isOpen={showMediaSelector}
          onClose={() => {
            setShowMediaSelector(false);
            fetchMediaFiles(); // Rafraîchir la liste après fermeture
          }}
          onSelect={(url) => {
            // Gérer la sélection simple (pas de multi-sélection dans MediaLibrary)
            if (typeof url === 'string') {
              copyToClipboard(url, 'Fichier sélectionné');
            }
          }}
          title="Médiathèque - Gérer vos fichiers"
          allowUpload={true}
        />
      </div>
    </AdminLayout>
  );
}
